import React, { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { useAffiliateAnalyticsData, type AnalyticsFilters } from '@/hooks/useAffiliateAnalyticsData';
import { useAuth } from '@/contexts/AuthContext';
import { format, subDays } from 'date-fns';
import { CalendarIcon, Search, Filter, TrendingUp, Users, DollarSign, Target } from 'lucide-react';
import { cn } from '@/lib/utils';

const AffiliateAnalytics = () => {
  const { profile } = useAuth();
  
  const [filters, setFilters] = useState<AnalyticsFilters>({
    dateRange: {
      from: subDays(new Date(), 365),
      to: new Date(),
    },
    conversionType: 'all',
    membershipType: 'all',
    searchTerm: '',
  });

  const { referralSources, conversionDetails, subscriptionAnalytics, loading, error } = 
    useAffiliateAnalyticsData(profile?.id, filters);

  const updateFilters = (key: keyof AnalyticsFilters, value: any) => {
    setFilters(prev => ({ ...prev, [key]: value }));
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(amount);
  };

  if (loading) {
    return (
      <div className="p-6 space-y-6">
        <div className="text-center">
          <h1 className="text-3xl font-bold tracking-tight text-primary-700">Analytics</h1>
          <p className="text-gray-600">Loading your detailed performance data...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6 space-y-6">
        <div className="text-center">
          <h1 className="text-3xl font-bold tracking-tight text-primary-700">Analytics</h1>
          <p className="text-red-600">Error: {error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      <div>
        <h1 className="text-3xl font-bold tracking-tight text-primary-700">Analytics Dashboard</h1>
        <p className="text-gray-600">
          Detailed tracking of your referral sources, conversions, and revenue.
        </p>
      </div>

      {/* Filters Section */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Filter className="h-5 w-5" />
            Filters & Date Range
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
            {/* Date Range Picker - Start Date */}
            <div className="space-y-2">
              <label className="text-sm font-medium">Start Date</label>
              <Popover>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    className={cn(
                      "w-full justify-start text-left font-normal",
                      !filters.dateRange?.from && "text-muted-foreground"
                    )}
                  >
                    <CalendarIcon className="mr-2 h-4 w-4" />
                    {filters.dateRange?.from ? (
                      format(filters.dateRange.from, "LLL dd, y")
                    ) : (
                      <span>Pick start date</span>
                    )}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start">
                  <Calendar
                    mode="single"
                    selected={filters.dateRange?.from}
                    onSelect={(date) => 
                      updateFilters('dateRange', { 
                        ...filters.dateRange, 
                        from: date || subDays(new Date(), 30)
                      })
                    }
                    initialFocus
                    className="p-3 pointer-events-auto"
                  />
                </PopoverContent>
              </Popover>
            </div>

            {/* Date Range Picker - End Date */}
            <div className="space-y-2">
              <label className="text-sm font-medium">End Date</label>
              <Popover>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    className={cn(
                      "w-full justify-start text-left font-normal",
                      !filters.dateRange?.to && "text-muted-foreground"
                    )}
                  >
                    <CalendarIcon className="mr-2 h-4 w-4" />
                    {filters.dateRange?.to ? (
                      format(filters.dateRange.to, "LLL dd, y")
                    ) : (
                      <span>Pick end date</span>
                    )}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start">
                  <Calendar
                    mode="single"
                    selected={filters.dateRange?.to}
                    onSelect={(date) => 
                      updateFilters('dateRange', { 
                        ...filters.dateRange, 
                        to: date || new Date()
                      })
                    }
                    disabled={(date) => 
                      filters.dateRange?.from ? date < filters.dateRange.from : false
                    }
                    initialFocus
                    className="p-3 pointer-events-auto"
                  />
                </PopoverContent>
              </Popover>
            </div>

            {/* Conversion Type Filter */}
            <div className="space-y-2">
              <label className="text-sm font-medium">Conversion Type</label>
              <Select 
                value={filters.conversionType} 
                onValueChange={(value) => updateFilters('conversionType', value)}
              >
                <SelectTrigger>
                  <SelectValue placeholder="All types" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Types</SelectItem>
                  <SelectItem value="coupon">Coupon Codes</SelectItem>
                  <SelectItem value="link">Referral Links</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Membership Type Filter */}
            <div className="space-y-2">
              <label className="text-sm font-medium">Membership Type</label>
              <Select 
                value={filters.membershipType} 
                onValueChange={(value) => updateFilters('membershipType', value)}
              >
                <SelectTrigger>
                  <SelectValue placeholder="All memberships" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Memberships</SelectItem>
                  <SelectItem value="subscription">Recurring</SelectItem>
                  <SelectItem value="one_time">One-time</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Search */}
            <div className="space-y-2">
              <label className="text-sm font-medium">Search Customer</label>
              <div className="relative">
                <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Name or email..."
                  value={filters.searchTerm}
                  onChange={(e) => updateFilters('searchTerm', e.target.value)}
                  className="pl-8"
                />
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Analytics Tabs */}
      <Tabs defaultValue="sources" className="space-y-4">
        <TabsList>
          <TabsTrigger value="sources">Referral Sources</TabsTrigger>
          <TabsTrigger value="conversions">Conversion Details</TabsTrigger>
          <TabsTrigger value="subscriptions">Subscription Analytics</TabsTrigger>
        </TabsList>

        {/* Referral Sources Tab */}
        <TabsContent value="sources">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Target className="h-5 w-5" />
                Referral Sources
              </CardTitle>
              <CardDescription>
                Track who clicked your links and which sources are converting best
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="rounded-md border">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Date/Time</TableHead>
                      <TableHead>Referral Source</TableHead>
                      <TableHead>UTM Campaign</TableHead>
                      <TableHead>Converted</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {referralSources.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={4} className="text-center py-8 text-gray-500">
                          No referral data found for the selected date range
                        </TableCell>
                      </TableRow>
                    ) : (
                      referralSources.map((source) => (
                        <TableRow key={source.id}>
                          <TableCell>
                            {format(new Date(source.clicked_at), 'MMM dd, yyyy HH:mm')}
                          </TableCell>
                          <TableCell>
                            <div className="max-w-xs truncate" title={source.referral_source}>
                              {source.referral_source}
                            </div>
                            {source.utm_source && (
                              <div className="text-xs text-gray-500">
                                {source.utm_source} / {source.utm_medium}
                              </div>
                            )}
                          </TableCell>
                          <TableCell>
                            {source.utm_campaign ? (
                              <Badge variant="outline">{source.utm_campaign}</Badge>
                            ) : (
                              <span className="text-gray-400">—</span>
                            )}
                          </TableCell>
                          <TableCell>
                            <Badge variant={source.did_convert ? "default" : "secondary"}>
                              {source.did_convert ? "Yes" : "No"}
                            </Badge>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Conversion Details Tab */}
        <TabsContent value="conversions">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Users className="h-5 w-5" />
                Conversion Details
              </CardTitle>
              <CardDescription>
                View detailed information about successful conversions
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="rounded-md border">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Customer</TableHead>
                      <TableHead>Type</TableHead>
                      <TableHead>Membership</TableHead>
                      <TableHead>Commission</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Date</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {conversionDetails.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={6} className="text-center py-8 text-gray-500">
                          No conversions found for the selected filters
                        </TableCell>
                      </TableRow>
                    ) : (
                      conversionDetails.map((conversion) => (
                        <TableRow key={conversion.id}>
                          <TableCell>
                            <div className="font-medium">{conversion.customer_name}</div>
                            <div className="text-xs text-gray-500">{conversion.customer_email}</div>
                          </TableCell>
                          <TableCell>
                            <Badge variant={conversion.conversion_type === 'coupon' ? "default" : "secondary"}>
                              {conversion.conversion_type === 'coupon' && conversion.referral_code 
                                ? `Coupon: ${conversion.referral_code}` 
                                : conversion.conversion_type === 'coupon' 
                                  ? 'Coupon' 
                                  : 'Link'}
                            </Badge>
                          </TableCell>
                          <TableCell>
                            <div className="font-medium">{conversion.membership_name || 'Unknown'}</div>
                            <Badge variant={conversion.membership_type === 'subscription' ? "default" : "outline"} className="text-xs">
                              {conversion.membership_type === 'subscription' ? 'Recurring' : 'One-time'}
                            </Badge>
                            {conversion.monthly_recurring_value && (
                              <div className="text-xs text-green-600">
                                MRR: {formatCurrency(conversion.monthly_recurring_value)}
                              </div>
                            )}
                          </TableCell>
                          <TableCell className="font-medium">
                            {formatCurrency(conversion.commission_earned)}
                          </TableCell>
                          <TableCell>
                            <Badge 
                              variant={conversion.status === 'active' ? "default" : 
                                      conversion.status === 'cancelled' ? "destructive" : "secondary"}
                            >
                              {conversion.status}
                            </Badge>
                          </TableCell>
                          <TableCell>
                            {format(new Date(conversion.earned_at), 'MMM dd, yyyy')}
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Subscription Analytics Tab */}
        <TabsContent value="subscriptions">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Active Subscriptions</CardTitle>
                <Users className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{subscriptionAnalytics.active_subscriptions}</div>
                <p className="text-xs text-muted-foreground">
                  Currently active recurring subscriptions
                </p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Monthly Recurring Revenue</CardTitle>
                <TrendingUp className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">
                  {formatCurrency(subscriptionAnalytics.monthly_recurring_revenue)}
                </div>
                <p className="text-xs text-muted-foreground">
                  Commission from recurring subscriptions
                </p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Lifetime Value per Customer</CardTitle>
                <DollarSign className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">
                  {formatCurrency(subscriptionAnalytics.lifetime_value_per_customer)}
                </div>
                <p className="text-xs text-muted-foreground">
                  Average commission per customer
                </p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">One-time Purchase Total</CardTitle>
                <DollarSign className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">
                  {formatCurrency(subscriptionAnalytics.one_time_purchase_total)}
                </div>
                <p className="text-xs text-muted-foreground">
                  Total commission from one-time purchases
                </p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Churn Rate</CardTitle>
                <TrendingUp className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{subscriptionAnalytics.churn_rate.toFixed(1)}%</div>
                <p className="text-xs text-muted-foreground">
                  Monthly subscription cancellation rate
                </p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Avg. Subscription Duration</CardTitle>
                <Users className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">
                  {subscriptionAnalytics.avg_subscription_duration.toFixed(1)} mo
                </div>
                <p className="text-xs text-muted-foreground">
                  Average subscription lifespan
                </p>
              </CardContent>
            </Card>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default AffiliateAnalytics;